import axios from "../utils/axiosConfig";
import { API_URL } from "../variables";

// Action types
export const COUPON_ACTIONS = {
  APPLY_COUPON_REQUEST: "APPLY_COUPON_REQUEST",
  APPLY_COUPON_SUCCESS: "APPLY_COUPON_SUCCESS",
  APPLY_COUPON_FAILURE: "APPLY_COUPON_FAILURE",
  REMOVE_COUPON: "REMOVE_COUPON",
  CLEAR_COUPONS: "CLEAR_COUPONS",
};

// Action creators
export const applyCouponRequest = () => ({
  type: COUPON_ACTIONS.APPLY_COUPON_REQUEST,
});

export const applyCouponSuccess = (coupon) => ({
  type: COUPON_ACTIONS.APPLY_COUPON_SUCCESS,
  payload: coupon,
});

export const applyCouponFailure = (error) => ({
  type: COUPON_ACTIONS.APPLY_COUPON_FAILURE,
  payload: error,
});

export const removeCoupon = (couponCode) => ({
  type: COUPON_ACTIONS.REMOVE_COUPON,
  payload: couponCode,
});

export const clearCoupons = () => ({
  type: COUPON_ACTIONS.CLEAR_COUPONS,
});

// Async action to apply coupon
export const applyCoupon = (couponCode) => {
  return async (dispatch, getState) => {
    dispatch(applyCouponRequest());

    try {
      // Get user ID from auth state
      const { user } = getState().auth;
      if (!user || !user.user_id) {
        dispatch(applyCouponFailure("User not authenticated"));
        return { success: false, error: "User not authenticated" };
      }

      const response = await axios.post(
        `${API_URL}/api/orders/coupons/validate`,
        {
          coupon_code: couponCode,
          user_id: user.user_id,
        }
      );

      if (response.data.status === "success") {
        dispatch(applyCouponSuccess(response.data.data));
        return { success: true, coupon: response.data.data };
      } else {
        dispatch(
          applyCouponFailure(response.data.message || "Coupon not found")
        );
        return {
          success: false,
          error: response.data.message || "Coupon not found",
        };
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to apply coupon";
      dispatch(applyCouponFailure(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
};

// Helper function to calculate discount
export const calculateDiscount = (coupon, cartTotal) => {
  if (!coupon) return 0;

  if (coupon.coupon_type === "percentage") {
    return (cartTotal * parseFloat(coupon.discount)) / 100;
  } else if (coupon.coupon_type === "fixed") {
    return parseFloat(coupon.discount);
  }

  return 0;
};

// Helper function to check if coupon conditions are met
export const checkCouponConditions = (coupon, userData = {}) => {
  if (!coupon || coupon.coupon_condition === "no_condition") {
    return { valid: true, message: "" };
  }

  switch (coupon.coupon_condition) {
    case "referral":
      // This will work for any referral code
      return { valid: true, message: "" };

    case "orders_number":
      const userOrderCount = userData.orderCount || 0;
      const requiredOrders = parseInt(coupon.orders_count);
      if (userOrderCount < requiredOrders) {
        return {
          valid: false,
          message: `This coupon requires at least ${requiredOrders} previous orders. You have ${userOrderCount}.`,
        };
      }
      return { valid: true, message: "" };

    case "amount_spent":
      const userTotalSpent = userData.totalSpent || 0;
      const requiredAmount = parseFloat(coupon.amount_threshold);
      if (userTotalSpent < requiredAmount) {
        return {
          valid: false,
          message: `This coupon requires at least ${requiredAmount} total spent. You have spent ${userTotalSpent}.`,
        };
      }
      return { valid: true, message: "" };

    case "provided_review":
      // This will work for people who have provided a review
      return { valid: true, message: "" };

    case "routine":
      // Routine validation is handled server-side as it requires cart data
      // If we reach here, the server already validated it
      return { valid: true, message: "" };

    default:
      return { valid: true, message: "" };
  }
};
